/**
 * Complete OAuth Registration Dialog
 *
 * Shown after the Google OAuth callback returns status=registration_required
 * for brand-new users. Collects the 10-digit mobile number that was not
 * available from Google, and POSTs it alongside the pending_token to the
 * backend completeRegistration endpoint. On success, stores auth data and
 * navigates the user into the app.
 */

import React, { useState } from 'react';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import { Message } from 'primereact/message';
import oauthService from '../../services/oauth.service';

const CompleteOAuthRegistrationDialog = ({
    visible,
    email,
    name,
    pendingToken,
    onSuccess,
    onClose,
}) => {
    const [mobile, setMobile] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);

    const validate = () => {
        if (!mobile) return 'Mobile number is required.';
        if (!/^[0-9]{10}$/.test(mobile)) return 'Mobile number must be exactly 10 digits.';
        return null;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError(null);

        const localError = validate();
        if (localError) {
            setError(localError);
            return;
        }

        setSubmitting(true);
        try {
            // oauth.service uses REACT_APP_CENTRAL_URL (or http://localhost:8000/api/central)
            // so this call hits the central domain, not the tenant subdomain.
            const result = await oauthService.completeRegistration(pendingToken, mobile);

            if (result.success) {
                onSuccess(result.data);
            } else {
                setError(result.message);

                // If the session expired, close the dialog so the user can re-initiate OAuth
                if (result.status === 410) {
                    setTimeout(() => onClose(), 2500);
                }
            }
        } finally {
            setSubmitting(false);
        }
    };

    const footer = (
        <div className="flex justify-content-end gap-2">
            <Button
                type="button"
                label="Cancel"
                icon="pi pi-times"
                className="p-button-text"
                onClick={onClose}
                disabled={submitting}
            />
            <Button
                type="submit"
                label={submitting ? 'Creating account...' : 'Continue'}
                icon="pi pi-check"
                loading={submitting}
                onClick={handleSubmit}
                disabled={submitting}
            />
        </div>
    );

    return (
        <Dialog
            header="Complete your signup"
            visible={visible}
            onHide={submitting ? () => {} : onClose}
            style={{ width: '460px' }}
            modal
            closable={!submitting}
            draggable={false}
            footer={footer}
        >
            <form onSubmit={handleSubmit}>
                <p className="text-600 line-height-3 mb-4">
                    We just need one more detail to finish creating your account.
                </p>

                <div className="field mb-3">
                    <label className="block font-medium mb-2">Email</label>
                    <InputText value={email || ''} disabled className="w-full" />
                </div>

                <div className="field mb-3">
                    <label className="block font-medium mb-2">Name</label>
                    <InputText value={name || ''} disabled className="w-full" />
                </div>

                <div className="field mb-3">
                    <label htmlFor="oauth-mobile" className="block font-medium mb-2">
                        Mobile number <span className="text-red-500">*</span>
                    </label>
                    <InputText
                        id="oauth-mobile"
                        type="tel"
                        value={mobile}
                        onChange={(e) => setMobile(e.target.value.replace(/\D/g, '').slice(0, 10))}
                        placeholder="10-digit mobile number"
                        className="w-full"
                        maxLength={10}
                        autoFocus
                    />
                    <small className="text-500 block mt-2">We'll use this to secure your account.</small>
                </div>

                {error && <Message severity="error" text={error} className="w-full mt-2" />}
            </form>
        </Dialog>
    );
};

export default CompleteOAuthRegistrationDialog;
